// src/pages/admin/AdminAuditPage.tsx
import React, { useState } from 'react';
import { useAuditLogs, useExportAuditLogs } from '../hooks';
import { AuditLogTable } from '../components';
import { useToast } from '@/core/hooks/useToast';
import { AuditAction, AuditResource } from '@/types/admin';

export const AdminAuditPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<{
    user_id?: string;
    action?: AuditAction;
    resource?: AuditResource;
    start_date?: string;
    end_date?: string;
  }>({});

  const { toast } = useToast();
  const { data, isLoading } = useAuditLogs({ page, limit: 50, ...filters });
  const exportMutation = useExportAuditLogs();

  const handleExport = () => {
    exportMutation.mutate({ format: 'csv', ...filters }, {
      onSuccess: () => toast({ title: 'Export started', variant: 'success' }),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold text-navy">Audit Logs</h1>
          <p className="text-warm-gray">Track all administrative actions and changes</p>
        </div>
        <button
          onClick={handleExport}
          disabled={exportMutation.isPending}
          className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark"
        >
          Export Logs
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <select
            value={filters.action || ''}
            onChange={(e) => setFilters(f => ({ ...f, action: e.target.value as AuditAction || undefined }))}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">All Actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
            <option value="suspend">Suspend</option>
            <option value="activate">Activate</option>
            <option value="approve">Approve</option>
            <option value="reject">Reject</option>
          </select>
          <select
            value={filters.resource || ''}
            onChange={(e) => setFilters(f => ({ ...f, resource: e.target.value as AuditResource || undefined }))}
            className="px-4 py-2 border border-gray-200 rounded-lg"
          >
            <option value="">All Resources</option>
            <option value="user">User</option>
            <option value="family">Family</option>
            <option value="child">Child</option>
            <option value="resource">Resource</option>
            <option value="vendor">Vendor</option>
            <option value="post">Post</option>
            <option value="review">Review</option>
          </select>
          <input
            type="date"
            value={filters.start_date || ''}
            onChange={(e) => setFilters(f => ({ ...f, start_date: e.target.value || undefined }))}
            className="px-4 py-2 border border-gray-200 rounded-lg"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={filters.end_date || ''}
            onChange={(e) => setFilters(f => ({ ...f, end_date: e.target.value || undefined }))}
            className="px-4 py-2 border border-gray-200 rounded-lg"
            placeholder="End Date"
          />
          <button
            onClick={() => setFilters({})}
            className="px-4 py-2 text-warm-gray hover:text-navy"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Logs Table */}
      <AuditLogTable
        logs={data?.items || []}
        isLoading={isLoading}
        onExport={handleExport}
      />

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-warm-gray">
            Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, data.total)} of {data.total} logs
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-200 rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}
              disabled={page === data.total_pages}
              className="px-3 py-1 border border-gray-200 rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAuditPage;



